const CAPABILITY_ROLES = {
  accessSettings: ['admin', 'director'],
  manageUsers: ['admin', 'director'],
  manageRolePermissions: ['admin'],
  accessAdminOnlyPanels: ['admin'],
  accessToolbox: ['admin'],
  manageEmailSettings: ['admin', 'director'],
  accessTroubleshooting: ['admin', 'director'],
  runTroubleshootingActions: ['admin', 'director'],
  manageAnomalies: ['admin', 'director'],
  completeReports: ['admin', 'director', 'manager'],
  viewPendingReports: ['admin', 'director', 'manager'],
  manageTechnicalSftp: ['admin'],
  manageRooms: ['admin', 'director'],
  viewRoomRevenueKpiBasis: ['admin', 'director'],
  manageRoomRevenueKpiBasis: ['admin'],
  selectEnterprise: ['director', 'manager', 'viewer'],
  assignProperties: ['manager', 'viewer'],
  adminSecurityBadge: ['admin'],
  purgeIngestedReports: ['admin'],
  viewMonthlySummary: ['admin', 'director', 'manager'],
  enterpriseSelectionRequired: ['director'],
};

const BACKEND_CAPABILITIES = new Set([
  'accessSettings',
  'manageUsers',
  'manageRolePermissions',
  'manageEmailSettings',
  'accessTroubleshooting',
  'runTroubleshootingActions',
  'manageAnomalies',
  'completeReports',
  'manualUploadReports',
  'regenerateReports',
  'manageTechnicalSftp',
  'manageRooms',
  'viewRoomRevenueKpiBasis',
  'manageRoomRevenueKpiBasis',
  'purgeIngestedReports',
  'viewMonthlySummary',
  'viewPropertyAudit',
  'viewAnomalyStats',
  'viewAllPropertyAnomalies',
  'accessSettings',
  'accessArchive',
  'accessDailyReports',
  'accessUserProfile',
  'accessOccupancyAnalysis',
  'downloadPdf',
]);

function extractRole(userOrRole) {
  if (!userOrRole) return null;
  if (typeof userOrRole === 'string') return userOrRole;
  return userOrRole.role || null;
}

function extractCapabilities(userOrRole) {
  if (!userOrRole || typeof userOrRole === 'string') return null;
  if (Array.isArray(userOrRole.capabilities)) return userOrRole.capabilities;
  if (Array.isArray(userOrRole.profile?.capabilities)) return userOrRole.profile.capabilities;
  return null;
}

function toBackendCapabilityName(capability) {
  return capability.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

export function hasRole(userOrRole, roles) {
  return roles.includes(extractRole(userOrRole));
}

export function hasCapability(userOrRole, capability) {
  const capabilities = extractCapabilities(userOrRole);
  if (capabilities && BACKEND_CAPABILITIES.has(capability)) {
    return capabilities.includes(toBackendCapabilityName(capability));
  }
  return hasRole(userOrRole, CAPABILITY_ROLES[capability] || []);
}

export function canAccessSettings(userOrRole) {
  return hasCapability(userOrRole, 'accessSettings');
}

export function canManageUsers(userOrRole) {
  return hasCapability(userOrRole, 'manageUsers');
}

export function canAccessToolbox(userOrRole) {
  return hasCapability(userOrRole, 'accessToolbox');
}

export function canAccessAdminOnlyPanels(userOrRole) {
  return hasCapability(userOrRole, 'accessAdminOnlyPanels');
}

export function canManageRolePermissions(userOrRole) {
  return hasCapability(userOrRole, 'manageRolePermissions');
}

export function canManageEmailSettings(userOrRole) {
  return hasCapability(userOrRole, 'manageEmailSettings');
}

export function canAccessTroubleshooting(userOrRole) {
  return hasCapability(userOrRole, 'accessTroubleshooting');
}

export function canRunTroubleshootingActions(userOrRole) {
  return hasCapability(userOrRole, 'runTroubleshootingActions');
}

export function canManageAnomalies(userOrRole) {
  return hasCapability(userOrRole, 'manageAnomalies');
}

export function canCompleteReports(userOrRole) {
  return hasCapability(userOrRole, 'completeReports');
}

export function canViewPendingReports(userOrRole) {
  return hasCapability(userOrRole, 'viewPendingReports');
}

export function canManageTechnicalSftp(userOrRole) {
  return hasCapability(userOrRole, 'manageTechnicalSftp');
}

export function canManageRoomRevenueKpiBasis(userOrRole) {
  return hasCapability(userOrRole, 'manageRoomRevenueKpiBasis') || hasRole(userOrRole, ['admin']);
}

export function canViewRoomRevenueKpiBasis(userOrRole) {
  return hasCapability(userOrRole, 'viewRoomRevenueKpiBasis') || hasRole(userOrRole, ['admin', 'director']);
}

export function canManageRooms(userOrRole) {
  return hasCapability(userOrRole, 'manageRooms');
}

export function shouldShowEnterpriseField(userOrRole) {
  return hasCapability(userOrRole, 'selectEnterprise');
}

export function shouldShowPropertyAssignments(userOrRole) {
  return hasCapability(userOrRole, 'assignProperties');
}

export function shouldClearAssignedProperties(userOrRole) {
  return !shouldShowPropertyAssignments(userOrRole);
}

export function shouldClearEnterpriseSelection(userOrRole) {
  return !shouldShowEnterpriseField(userOrRole);
}

export function hasAdminSecurityBadge(userOrRole) {
  return hasCapability(userOrRole, 'adminSecurityBadge');
}

export function canPurgeIngestedReports(userOrRole) {
  return hasCapability(userOrRole, 'purgeIngestedReports');
}

export function canViewMonthlySummary(userOrRole) {
  return hasCapability(userOrRole, 'viewMonthlySummary');
}

export function isEnterpriseSelectionRequired(userOrRole) {
  return hasCapability(userOrRole, 'enterpriseSelectionRequired');
}